import { useState, useEffect, useRef } from 'react'
import ReilmLogo from '../components/ReilmLogo'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from './auth/AuthContext'

const Navbar = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, logout } = useAuth()

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const headerRef = useRef(null)

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  // Review #3, M3.1: the burger toggled itself and nothing else closed the menu, so a press
  // anywhere on the page left it hanging over the content. Both listeners are bound only while it
  // is open, and `pointerdown` rather than `click` so the menu is gone before the press lands on
  // whatever is underneath. The toggle button is inside the header, so the containment check keeps
  // a second press on the burger working as a close rather than closing and immediately reopening.
  useEffect(() => {
    if (!menuOpen) return
    const onPointerDown = (e) => {
      if (!headerRef.current?.contains(e.target)) setMenuOpen(false)
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  // D1.1.2: the navbar used to flip state on a single threshold (scrollY > 20). Because
  // it is sticky, changing its own height moves the document under it, which can push the
  // scroll position back across that same threshold — so it oscillated instead of
  // settling. A hysteresis band fixes it: shrink past 48, expand only back under 12, and
  // nothing in between can flip the state.
  useEffect(() => {
    const SHRINK_AT = 48
    const EXPAND_AT = 12
    const onScroll = () =>
      setScrolled((was) => (was ? window.scrollY > EXPAND_AT : window.scrollY > SHRINK_AT))
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const toLanding = () => navigate('/')

  // Review #3, M4.2: this used to swap to Pipeline once signed in. That was our reading of set #2's
  // item about "Pricing" showing after sign-up — but that item was about the navbar *inside* the
  // platform, and applying it here took the public site with it. Микола's reason for reverting is
  // the one already noted below: the Dashboard button beside it goes to the platform, so the link
  // has no reason to duplicate it. Always Pricing.
  const navLink = { to: '/pricing', label: 'Pricing' }

  return (
    <header
      ref={headerRef}
      className={`sticky top-0 z-30 bg-brand-pageFill/95 backdrop-blur border-b border-brand-muted transition-[height] duration-200 ${
        /* Spec 29.09 §2.2 N1-N2: 80 at rest, 64 sticky on desktop. Mobile sticky is not drawn (M1), live 56 kept. */
        scrolled ? 'h-[56px] md:h-[64px]' : 'h-[72px] md:h-[80px]'
      }`}
    >
      <div className="h-full max-w-canvas mx-auto px-4 md:px-6 canvas:px-0 flex items-center justify-between gap-3">
        {/* N13: the burger sits left of the logo on mobile. */}
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          className="md:hidden w-[40px] h-[40px] flex items-center justify-center rounded-full hover:bg-black/5 shrink-0"
        >
          {menuOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M6 6l12 12M18 6L6 18" stroke="#17270D" strokeWidth="2" strokeLinecap="round" />
            </svg>
          ) : (
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M4 6.67h24M4 16h24M4 25.33h24" stroke="#17270D" strokeWidth="2" strokeLinecap="round" />
            </svg>
          )}
        </button>

        <button
          type="button"
          onClick={toLanding}
          className="flex items-center shrink-0 group mr-auto md:mr-0"
          aria-label="Arvance home"
        >
          {/* D3.1: the scale hover was ours; Figma also recolours the logo on hover, so the
              two now run together. Landing was not the page Микола filed D3.1 against — it is
              the same logo and the same spec node, and he reviews this navbar closely. */}
          <ReilmLogo
            className={`w-[131px] h-[28px] transition-all duration-200 group-hover:scale-[1.04] ${
              scrolled ? 'md:w-[150px] md:h-[32px]' : 'md:w-[168px] md:h-[36px]'
            }`}
          />
        </button>

        {/* N6-N7: 16/700; the current page keeps dark text with a 2px green underline 9px under
            the baseline. Hover is not drawn (M4), the live green hover stays. */}
        <nav className="hidden md:flex items-center gap-12 text-[16px] leading-6 font-bold">
          <Link
            to={navLink.to}
            className={`relative transition ${
              location.pathname === navLink.to
                ? 'text-brand-dark after:absolute after:left-0 after:right-0 after:top-[calc(100%+2.5px)] after:h-[2px] after:bg-brand-primary'
                : 'text-brand-dark hover:text-brand-primary'
            }`}
          >
            {navLink.label}
          </Link>
        </nav>

        <div className="hidden md:flex items-center gap-2 shrink-0">
          {isAuthenticated ? (
            <>
              <button
                onClick={() => navigate('/pipeline')}
                className={`text-[14px] px-[23px] rounded-full border border-brand-fieldBdr text-brand-dark font-semibold hover:text-[#4CBC18] hover:border-[#4CBC18] transition-all ${scrolled ? 'h-[36px]' : 'h-[40px]'}`}
              >
                Dashboard
              </button>
              <button
                onClick={handleLogout}
                className={`text-[14px] px-6 rounded-full bg-brand-primary hover:bg-brand-hover text-white font-semibold whitespace-nowrap transition-all ${scrolled ? 'h-[36px]' : 'h-[40px]'}`}
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate('/login')}
                className={`text-[14px] px-[23px] rounded-full border border-brand-fieldBdr text-brand-dark font-semibold hover:text-[#4CBC18] hover:border-[#4CBC18] transition-all ${scrolled ? 'h-[36px]' : 'h-[40px]'}`}
              >
                Login
              </button>
              <button
                onClick={() => navigate('/signup')}
                className={`text-[14px] px-6 rounded-full bg-brand-primary hover:bg-brand-hover text-white font-semibold whitespace-nowrap transition-all ${scrolled ? 'h-[36px]' : 'h-[40px]'}`}
              >
                Start Your Free Trial
              </button>
            </>
          )}
        </div>

      </div>

      {menuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 z-40 bg-brand-pageFill border-b border-black/5 shadow-md">
          <div className="flex flex-col gap-3 px-6 py-5">
            <Link
              to={navLink.to}
              className={`text-[16px] font-semibold py-2 ${
                location.pathname === navLink.to ? 'text-brand-primary' : 'text-brand-dark'
              }`}
            >
              {navLink.label}
            </Link>
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => navigate('/pipeline')}
                  className="h-[44px] rounded-full border border-brand-dark/20 text-brand-dark text-[14px] font-semibold"
                >
                  Dashboard
                </button>
                <button
                  onClick={handleLogout}
                  className="h-[44px] rounded-full bg-brand-primary text-white text-[14px] font-semibold"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/login')}
                  className="h-[44px] rounded-full border border-brand-dark/20 text-brand-dark text-[14px] font-semibold"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate('/signup')}
                  className="h-[44px] rounded-full bg-brand-primary text-white text-[14px] font-semibold"
                >
                  Start Your Free Trial
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  )
}

export default Navbar
